import { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { IMultifactorAuthenticationUserSettings } from "@/Common/Models/UI/IMultifactorAuthenticationUserSettings";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { Button, LinkButton } from "@/DesignSystem";
import { ExpandableSection } from "../ExpandableSection";
import { Modal } from "../Modals/Modal";
import { displayToast, MessageToast } from "../Toast/Toast";
import { MultifactorAuthenticationInput } from "./MultifactorAuthenticationInput";

import styles from "./NewFlowMultifactorAuthenticationSetupPopup.module.scss";

interface INewFlowMultifactorAuthenticationPopupProps {
    authenticationSettings: IMultifactorAuthenticationUserSettings;
    lastVerifiedEmail: string | null;
    maskEmail?: boolean;
    onSave: (setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod) => void;
    onCancel: () => void;
}

interface IMultifactorAuthenticatorSetupInfo {
    /**
     * The token used to verify and enable the authenticator.
     */
    setupToken: string;

    /**
     * The image URL for a QR code which can be scanned to configure an authenticator app.
     */
    codeImageUrl?: string;

    /**
     * The code which can be entered manually into an authenticator app if they can't scan the {@link CodeImageUrl}.
     */
    manualCode?: string;
}

interface IVerifySetupInfoResult {
    isSuccess: boolean;
    errors: string[];
}

class NewFlowMultifactorAuthenticationSetupUtils {
    public static async generateSetupInfo(): Promise<IMultifactorAuthenticatorSetupInfo> {
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_GenerateSetupInfo,
            { }
        );

        return response.isSuccess
            ? this.parseSetupResponse(response)
            : null;
    }

    public static async sendSetupCodeByEmail(setupToken: string): Promise<boolean> {
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_SendSetupCodeByEmail,
            { SetupToken: setupToken }
        );

        return response.isSuccess;
    }

    public static async verifySetupInfo(setupToken: string, authenticationCode: string): Promise<IVerifySetupInfoResult> {
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_VerifySetupInfo,
            {
                AuthenticationCode: authenticationCode,
                SetupToken: setupToken,
                CodeDeliveryMethod: AuthenticatorCodeDeliveryMethod.Application
            }
        );

        return {
            isSuccess: response.isSuccess,
            errors: sessionStore.formatErrorsForDisplay(response.errors)
        };
    }

    private static parseSetupResponse(response: ClientResponse<any>): IMultifactorAuthenticatorSetupInfo {
        return {
            setupToken: response.data.SetupToken,
            codeImageUrl: response.data.CodeImageUrl,
            manualCode: response.data.ManualCode
        };
    }
}

function NewFlowMultifactorAuthenticationSetupPopup(props: Readonly<INewFlowMultifactorAuthenticationPopupProps>): JSX.Element {
    const { getText } = useTranslation();

    const shouldShowEmailSection = !IsNullOrWhiteSpace(props.lastVerifiedEmail) && cacheStore.ClinicSettings.EnableMultifactorAuthenticationByEmail;

    const [openSections, setOpenSections] = useState(
        shouldShowEmailSection && props.authenticationSettings.CodeDeliveryMethod === AuthenticatorCodeDeliveryMethod.Email
            ? { application: false, email: true }
            : { application: true, email: false }
    );

    const [appSetupInfo, setAppSetupInfo] = useState<IMultifactorAuthenticatorSetupInfo>(null);
    const [emailSetupInfo, setEmailSetupInfo] = useState<IMultifactorAuthenticatorSetupInfo>(null);

    const [appAuthenticationCode, setAppAuthenticationCode] = useState<string>("");
    const [appVerifyResult, setAppVerifyResult] = useState<IVerifySetupInfoResult>({ isSuccess: false, errors: [] });

    const [emailAuthenticationCode, setEmailAuthenticationCode] = useState<string>("");
    const [emailVerifyResult, setEmailVerifyResult] = useState<IVerifySetupInfoResult>({ isSuccess: false, errors: [] });

    const [emailCodeSending, setEmailCodeSending] = useState({ codeSent: false, sendDisabled: false });

    const generateAppSetupInfo = useCallback(async () => {
        const setupInfo = await NewFlowMultifactorAuthenticationSetupUtils.generateSetupInfo();

        setAppSetupInfo(setupInfo);
    }, []);

    useEffect(
        () => {
            if (props.authenticationSettings.IsEnabled) {
                setAppSetupInfo(null);
                return;
            }

            generateAppSetupInfo();
        },
        [props.authenticationSettings.IsEnabled, generateAppSetupInfo]
    );

    // display only the first and last characters of the email user part. e.g. d*********s@medfarsolutions.com
    const displayEmail = useMemo(
        () => props.maskEmail
            ? props.lastVerifiedEmail?.replace(/(.).*(.)@/g, "$1**********$2@")
            : props.lastVerifiedEmail,
        [props.lastVerifiedEmail, props.maskEmail]
    );

    async function onAppAuthenticatorCodeSubmit(code: string): Promise<void> {
        setAppAuthenticationCode(code);
        appVerify(code);
    }

    async function appVerify(authenticationCode: string): Promise<void> {
        const result = await NewFlowMultifactorAuthenticationSetupUtils.verifySetupInfo(appSetupInfo?.setupToken, authenticationCode);

        setAppVerifyResult(result);
    }

    async function onEmailAuthenticatorCodeSubmit(code: string): Promise<void> {
        setEmailAuthenticationCode(code);
        emailVerify(code);
    }

    async function emailVerify(authenticationCode: string): Promise<void> {
        if (!emailSetupInfo?.setupToken)
            return;

        const result = await NewFlowMultifactorAuthenticationSetupUtils.verifySetupInfo(emailSetupInfo?.setupToken, authenticationCode);

        setEmailVerifyResult(result);
    }

    async function sendSetupCodeByEmail(): Promise<void> {
        let setupToken = emailSetupInfo?.setupToken;

        if (!setupToken) {
            const setupInfo = await NewFlowMultifactorAuthenticationSetupUtils.generateSetupInfo();

            setEmailSetupInfo(setupInfo);

            setupToken = setupInfo.setupToken;
        }

        const result = await NewFlowMultifactorAuthenticationSetupUtils.sendSetupCodeByEmail(setupToken);

        if (!result) {
            displayToast(MessageToast.Error, getText("InternalServerError"));
            return;
        }

        // disable the button and show the toast for 3 seconds
        const resendCodeTimeout = 3000;

        setEmailCodeSending(state => ({ ...state, codeSent: true, sendDisabled: true }));

        setTimeout(() => setEmailCodeSending(state => ({ ...state, sendDisabled: false })), resendCodeTimeout);

        displayToast(MessageToast.Success, getText("MultifactorAuthentication_CodeSentLabel"), {
            hideProgressBar: false,
            pauseOnFocusLoss: false,
            autoClose: resendCodeTimeout,
            pauseOnHover: false,
            closeOnClick: false
        });
    }

    function onSave(): void {
        if (appVerifyResult.isSuccess) {
            props.onSave(appSetupInfo.setupToken, appAuthenticationCode, AuthenticatorCodeDeliveryMethod.Application);
        }
        else if (emailVerifyResult.isSuccess) {
            props.onSave(emailSetupInfo.setupToken, emailAuthenticationCode, AuthenticatorCodeDeliveryMethod.Email);
        }
    }

    function renderFooter(): JSX.Element {
        const verified = appVerifyResult.isSuccess || emailVerifyResult.isSuccess;

        return (
            <div className={styles.footerContainer}>
                <Button
                    variant="neutral"
                    category="secondary"
                    onClick={() => props.onCancel()}
                    label={getText("CancelLabel")}
                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-Footer-Cancel"
                />
                <Button
                    variant="brand"
                    category="primary"
                    label={getText("SaveLabel")}
                    onClick={() => onSave()}
                    disabled={!verified}
                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-Footer-Save"
                />
            </div>
        );
    }

    function toggleApplicationMultifactorSectionOpen(): void {
        if (openSections.application) {
            setOpenSections(state => ({ ...state, application: false }));
            return;
        }

        if (props.authenticationSettings.IsEnabled) {
            setOpenSections(state => ({ ...state, application: true }));
        }
        else {
            // only allow one section open at a time during initial set up
            setOpenSections({ application: true, email: false });
        }
    }

    function toggleEmailMultifactorSectionOpen(): void {
        if (openSections.email) {
            setOpenSections(state => ({ ...state, email: false }));
            return;
        }

        if (props.authenticationSettings.IsEnabled) {
            setOpenSections(state => ({ ...state, email: true }));
        }
        else {
            // only allow one section open at a time during initial set up
            setOpenSections({ application: false, email: true });
        }
    }

    function renderAuthenticatorAppSetup(): JSX.Element {
        if (props.authenticationSettings.IsEnabled && !appSetupInfo?.setupToken) {
            return (
                <ExpandableSection
                    title={getText("MultifactorAuthenticationPopupLabel_AuthenticatorAppLabel")}
                    isOpen={openSections.application}
                    showChevronIcon={shouldShowEmailSection}
                    lockOpen={!shouldShowEmailSection}
                    onChevronClick={() => toggleApplicationMultifactorSectionOpen()}
                    chevronLabel={<strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>}
                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppActivatedSection"
                >
                    <ReactMarkdown>
                        {getText("MultifactorAuthentication_ResetPromptMarkdown")}
                    </ReactMarkdown>

                    <div className={styles.resetMultifactorAuthenticationLink}>
                        <LinkButton
                            label={getText("MultifactorAuthentication_ResetLinkLabel")}
                            underline={false}
                            onClick={() => generateAppSetupInfo()}
                            dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppActivatedSection-Reset"
                        />
                    </div>
                </ExpandableSection>
            );
        }

        const manualCodeWithSpaces = appSetupInfo?.manualCode?.match(/.{1,8}/g)?.join(" ");

        return (
            <ExpandableSection
                title={getText("MultifactorAuthenticationPopupLabel_AuthenticatorAppLabel")}
                isOpen={openSections.application}
                showChevronIcon={shouldShowEmailSection}
                lockOpen={!shouldShowEmailSection}
                onChevronClick={() => toggleApplicationMultifactorSectionOpen()}
                chevronLabel={appVerifyResult.isSuccess &&
                    <strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>
                }
                dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppSetupSection"
            >
                <div className={styles.expandedSection}>
                    <div className={styles.expandedContent}>
                        <p className={styles.bold}>{getText("MultifactorAuthenticationPopupLabel_Step1Label")}</p>
                        <p className={styles.expandedContentStepStart}>{getText("MultifactorAuthenticationPopupLabel_Step1")}</p>
                        <p className={styles.expandedContentStepLine}>{getText("MultifactorAuthenticationPopupLabel_Step1Alternative")}</p>
                        <p data-cy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppSetupSection-CodeText">
                            {manualCodeWithSpaces}
                        </p>
                        <p className={`${styles.bold} ${styles.expandedContentStepStart}`}>{getText("MultifactorAuthenticationPopupLabel_Step2Label")}</p>
                        <p className={styles.expandedContentStepLine}>{getText("MultifactorAuthenticationPopupLabel_Step2")}</p>
                        <div className={styles.appVerificationCodeGrid}>
                            <div>{getText("MultifactorAuthenticationPopupLabel_VerificationCode")}</div>
                            <div>
                                <MultifactorAuthenticationInput
                                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppSetupSection-MultifactorAuthenticationInput"
                                    disabled={appVerifyResult.isSuccess}
                                    isInvalid={!appVerifyResult.isSuccess}
                                    onChange={text => setAppAuthenticationCode(text)}
                                    onSubmit={text => onAppAuthenticatorCodeSubmit(text)}
                                    autoSubmit
                                />
                            </div>
                            <Button
                                disabled={appVerifyResult.isSuccess}
                                category="primary"
                                label={getText("VerifyLabel")}
                                onClick={() => appVerify(appAuthenticationCode)}
                                dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppSetupSection-VerificationCodeSubmit"
                            />
                            {appVerifyResult.errors?.length > 0 && (
                                <>
                                    <div />
                                    {appVerifyResult.errors.map((error, index) => (
                                        <div key={index + "," + error} className={styles.verificationCodeError}>
                                            {error}
                                        </div>
                                    ))}
                                    <div />
                                </>
                            )}
                        </div>
                    </div>
                    {appSetupInfo?.codeImageUrl && (
                        <img
                            src={appSetupInfo.codeImageUrl}
                            alt={`${getText("MultifactorAuthenticationPopupLabel_Step1Alternative")}: ${manualCodeWithSpaces}`}
                            data-cy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorAppSetupSection-CodeImage"
                        />
                    )}
                </div>
            </ExpandableSection>
        );
    }

    function renderAuthenticatorEmailSetup(): JSX.Element {
        if (props.authenticationSettings.IsEnabled && !emailSetupInfo?.setupToken) {
            return (
                <ExpandableSection
                    title={getText("EmailLabel")}
                    isOpen={openSections.email}
                    onChevronClick={() => toggleEmailMultifactorSectionOpen()}
                    chevronLabel={<strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>}
                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorEmailSetupSection"
                >
                    {displayEmail}
                </ExpandableSection>
            );
        }

        return (
            <ExpandableSection
                title={getText("EmailLabel")}
                isOpen={openSections.email}
                onChevronClick={() => toggleEmailMultifactorSectionOpen()}
                chevronLabel={emailVerifyResult.isSuccess &&
                    <strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>
                }
                dataCy="NewFlowMultifactorAuthenticationSetupPopup-AppSetup-AuthenticatorEmailSetupSection"
            >
                <div className={styles.expandedSection}>
                    <div className={styles.expandedContent}>
                        <p className={styles.bold}>{getText("MultifactorAuthenticationPopupLabel_EmailOnly_Introduction")}</p>
                        <p className={styles.bold}>{getText("MultifactorAuthenticationPopupLabel_Step1Label")}</p>
                        <p className={styles.expandedContentStepStart}>{getText("MultifactorAuthenticationPopupLabel_EmailOnly_SendCodeMessage")}</p>
                        <div className={styles.sendCodeGrid}>
                            <div data-cy="NewFlowMultifactorAuthenticationSetupPopup-EmailSetup-AuthenticatorAppSetupSection-DisplayEmail">{displayEmail}</div>
                            <Button
                                disabled={emailVerifyResult.isSuccess || emailCodeSending.sendDisabled}
                                category="primary"
                                label={getText(
                                    emailCodeSending.codeSent
                                        ? "MultifactorAuthenticationPopupLabel_EmailOnly_ResendCodeButton"
                                        : "MultifactorAuthenticationPopupLabel_EmailOnly_SendCodeButton"
                                )}
                                onClick={() => sendSetupCodeByEmail()}
                                dataCy="NewFlowMultifactorAuthenticationSetupPopup-EmailSetup-AuthenticatorAppSetupSection-SendCode"
                            />
                        </div>
                        <p className={`${styles.bold} ${styles.expandedContentStepStart}`}>{getText("MultifactorAuthenticationPopupLabel_Step2Label")}</p>
                        <p className={styles.expandedContentStepLine}>{getText("MultifactorAuthenticationPopupLabel_EmailOnly_VerifyMessage")}</p>
                        <div className={styles.emailVerificationCodeGrid}>
                            <div>{getText("MultifactorAuthenticationPopupLabel_VerificationCode")}</div>
                            <div>
                                <MultifactorAuthenticationInput
                                    dataCy="NewFlowMultifactorAuthenticationSetupPopup-EmailSetup-AuthenticatorAppSetupSection-MultifactorAuthenticationInput"
                                    disabled={emailVerifyResult.isSuccess || !emailSetupInfo}
                                    isInvalid={!emailVerifyResult.isSuccess}
                                    onChange={text => setEmailAuthenticationCode(text)}
                                    onSubmit={text => onEmailAuthenticatorCodeSubmit(text)}
                                    autoSubmit
                                />
                            </div>
                            <Button
                                disabled={emailVerifyResult.isSuccess || !emailSetupInfo}
                                category="primary"
                                label={getText("VerifyLabel")}
                                onClick={() => emailVerify(appAuthenticationCode)}
                                dataCy="NewFlowMultifactorAuthenticationSetupPopup-EmailSetup-AuthenticatorAppSetupSection-VerificationCodeSubmit"
                            />
                            {emailVerifyResult.errors?.length > 0 && (
                                <>
                                    <div />
                                    {emailVerifyResult.errors.map((error, index) => (
                                        <div key={index + "," + error} className={styles.verificationCodeError}>
                                            {error}
                                        </div>
                                    ))}
                                    <div />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </ExpandableSection>
        );
    }

    return (
        <Modal
            width={700}
            centerContent
            fixedOverlayPosition
            showOverlay
            disableOverlayClose
            showX
            onClose={props.onCancel}
            title={getText("MultifactorAuthenticationPopup_Title")}
            footer={renderFooter()}
            cssClass={styles.multifactorAuthenticationPopup}
            dataCy="NewFlowMultifactorAuthenticationSetupPopup-Modal"
        >
            <p>{getText("MultifactorAuthenticationPopupLabel_Introduction")}</p>
            {renderAuthenticatorAppSetup()}
            {shouldShowEmailSection && renderAuthenticatorEmailSetup()}
        </Modal>
    );
}

export { NewFlowMultifactorAuthenticationSetupPopup, NewFlowMultifactorAuthenticationSetupUtils };
